import { BRAND_ASSETS, BRAND_COLORS } from "./brandAssets";
import QRCode from "qrcode";
import {
  createThemeFoil,
  getShareCardTheme,
  resolveShareCardTag,
  resolveShareCardTier,
} from "./triviaShareCardTiers";

/** Portrait collectible, same ratio as the Joyput founding card. */
export const SHARE_CARD_WIDTH = 760;
export const SHARE_CARD_HEIGHT = 1064;
export const SHARE_CARD_SCALE = 2;

const NAVY = BRAND_COLORS.navy;
const INK = "#f3ede0";
const MUTED = "#8b8f98";

export function buildShareCardTitle(mode) {
  if (mode === "daily") return "Daily Challenge";
  if (mode === "custom") return "Custom Trivia";
  return "Trivia";
}

export function buildShareCardHeadline(sportLabel, mode) {
  const title = buildShareCardTitle(mode);
  if (!sportLabel) return title;
  return `${sportLabel} ${title}`;
}

export function buildVerifyPath(token) {
  if (!token) return null;
  return `/v/${token}`;
}

export function buildVerifyUrl(origin, token) {
  const path = buildVerifyPath(token);
  if (!path || !origin) return null;
  return `${String(origin).replace(/\/$/, "")}${path}`;
}

export function formatShareCardLink(token) {
  if (!token) return null;
  return `stats.fan/v/${token}`;
}

export function formatShareCardPercent(correct, total) {
  const made = Number(correct);
  const outOf = Number(total);
  if (!Number.isFinite(made) || !Number.isFinite(outOf) || outOf <= 0) return "—";
  return `${Math.round((made / outOf) * 100)}%`;
}

export function spacedWordmark(text) {
  return String(text || "STATS.FAN")
    .split("")
    .join(" ");
}

export function loadShareImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load ${src}`));
    image.src = src;
  });
}

export async function generateShareQrDataUrl(verifyUrl) {
  return QRCode.toDataURL(verifyUrl, {
    margin: 1,
    width: 280,
    color: { dark: NAVY, light: "#ffffff" },
    errorCorrectionLevel: "M",
  });
}

export function hexRgb(hex) {
  const n = String(hex || "#84E6E5").replace("#", "");
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

export function isQrFinderCell(x, y, count) {
  return (
    (x < 7 && y < 7) ||
    (x >= count - 7 && y < 7) ||
    (x < 7 && y >= count - 7)
  );
}

export function stackYs(top, bottom, count) {
  if (count <= 1) return [top];
  const step = (bottom - top) / (count - 1);
  return Array.from({ length: count }, (_, index) => top + step * index);
}

export function shareCardGradeTop(medalY, medalR, ringExtra = 13) {
  return medalY + medalR + ringExtra + 44;
}

export function logoMarkStrength(red, green, blue, alpha) {
  const luma = (red * 0.3 + green * 0.59 + blue * 0.11) / 255;
  if (alpha < 12 || luma < 0.08) return 0;
  return Math.min(1, luma * 1.45);
}

function blurHeightMap(source, size, passes = 3) {
  let current = source;
  let next = new Float32Array(source.length);
  for (let pass = 0; pass < passes; pass += 1) {
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        let sum = 0;
        let count = 0;
        for (let dy = -2; dy <= 2; dy += 1) {
          for (let dx = -2; dx <= 2; dx += 1) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= size || yy >= size) continue;
            sum += current[yy * size + xx];
            count += 1;
          }
        }
        next[y * size + x] = sum / count;
      }
    }
    const swap = current;
    current = next;
    next = swap;
  }
  return current;
}

export function engraveLogoCanvas(logo, size, metalHex) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx || !logo?.width) return canvas;

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, size, size);
  const pad = Math.round(size * 0.1);
  const dest = size - pad * 2;
  const side = Math.min(logo.width, logo.height);
  ctx.drawImage(
    logo,
    (logo.width - side) / 2,
    (logo.height - side) / 2,
    side,
    side,
    pad,
    pad,
    dest,
    dest
  );

  const image = ctx.getImageData(0, 0, size, size);
  const heights = new Float32Array(size * size);
  for (let i = 0; i < size * size; i += 1) {
    const p = i * 4;
    heights[i] = 1 - logoMarkStrength(image.data[p], image.data[p + 1], image.data[p + 2], image.data[p + 3]);
  }
  const carved = blurHeightMap(heights, size, 3);
  const [metalR, metalG, metalB] = hexRgb(metalHex);
  const lightX = -0.52;
  const lightY = -0.62;
  const lightZ = 0.58;
  const lightLen = Math.hypot(lightX, lightY, lightZ);
  const lx = lightX / lightLen;
  const ly = lightY / lightLen;
  const lz = lightZ / lightLen;

  for (let y = 1; y < size - 1; y += 1) {
    for (let x = 1; x < size - 1; x += 1) {
      const i = y * size + x;
      const height = carved[i];
      const dx = (carved[i + 1] - carved[i - 1]) * 4.2;
      const dy = (carved[(y + 1) * size + x] - carved[(y - 1) * size + x]) * 4.2;
      let nx = -dx;
      let ny = -dy;
      let nz = 1;
      const nlen = Math.hypot(nx, ny, nz) || 1;
      nx /= nlen;
      ny /= nlen;
      nz /= nlen;
      const diffuse = Math.max(0, nx * lx + ny * ly + nz * lz);
      const hx = lx;
      const hy = ly;
      const hz = lz + 1;
      const hlen = Math.hypot(hx, hy, hz) || 1;
      const spec = (nx * hx + ny * hy + nz * hz) / hlen;
      const shine = spec > 0 ? spec ** 26 * 210 : 0;
      const pit = (1 - height) * 0.42;
      const shade = Math.max(0.08, 0.2 + diffuse * 0.78 - pit);
      const p = i * 4;
      image.data[p] = Math.min(255, (88 + metalR * 0.62) * shade + shine);
      image.data[p + 1] = Math.min(255, (92 + metalG * 0.62) * shade + shine);
      image.data[p + 2] = Math.min(255, (96 + metalB * 0.62) * shade + shine);
      image.data[p + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

export function drawStyledQr(ctx, x, y, size, text, theme) {
  if (!text) return;
  const qr = QRCode.create(text, { errorCorrectionLevel: "M" });
  const count = qr.modules.size;
  const quiet = 2;
  const cell = size / (count + quiet * 2);
  const originX = x + quiet * cell;
  const originY = y + quiet * cell;

  ctx.fillStyle = "#f3fbfb";
  roundRect(ctx, x, y, size, size, 16);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = theme.tagStroke;
  ctx.stroke();

  ctx.fillStyle = NAVY;
  for (let row = 0; row < count; row += 1) {
    for (let col = 0; col < count; col += 1) {
      if (!qr.modules.get(col, row) || isQrFinderCell(col, row, count)) continue;
      roundRect(
        ctx,
        originX + col * cell + cell * 0.1,
        originY + row * cell + cell * 0.1,
        cell * 0.8,
        cell * 0.8,
        cell * 0.28
      );
      ctx.fill();
    }
  }

  const drawFinder = (col, row) => {
    const left = originX + col * cell;
    const top = originY + row * cell;
    ctx.fillStyle = NAVY;
    roundRect(ctx, left, top, cell * 7, cell * 7, cell * 1.15);
    ctx.fill();
    ctx.fillStyle = "#f3fbfb";
    roundRect(ctx, left + cell, top + cell, cell * 5, cell * 5, cell * 0.85);
    ctx.fill();
    ctx.fillStyle = theme.accent;
    roundRect(ctx, left + cell * 2, top + cell * 2, cell * 3, cell * 3, cell * 0.7);
    ctx.fill();
  };
  drawFinder(0, 0);
  drawFinder(count - 7, 0);
  drawFinder(0, count - 7);
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function drawMedallion(ctx, logo, mx, my, radius, theme) {
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 18;
  ctx.beginPath();
  ctx.arc(mx, my, radius, 0, Math.PI * 2);
  ctx.fillStyle = "#000";
  ctx.fill();
  ctx.restore();

  const ring = ctx.createConicGradient
    ? ctx.createConicGradient(3.7, mx, my)
    : null;
  if (ring) {
    theme.ringStops.forEach(([stop, color]) => ring.addColorStop(stop, color));
    ctx.fillStyle = ring;
  } else {
    ctx.fillStyle = theme.accent;
  }
  ctx.beginPath();
  ctx.arc(mx, my, radius + 13, 0, Math.PI * 2);
  ctx.fill();

  ctx.lineWidth = 4;
  ctx.strokeStyle = theme.ringHighlight;
  ctx.beginPath();
  ctx.arc(mx, my, radius + 11, Math.PI * 1.1, Math.PI * 1.9);
  ctx.stroke();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.45)";
  ctx.beginPath();
  ctx.arc(mx, my, radius + 11, Math.PI * 0.1, Math.PI * 0.9);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(mx, my, radius + 4, 0, Math.PI * 2);
  ctx.fillStyle = "#0a0c0f";
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(mx, my, radius, 0, Math.PI * 2);
  ctx.clip();
  if (logo?.width) {
    const engraved = engraveLogoCanvas(logo, Math.round(radius * 4), theme.accent);
    ctx.drawImage(engraved, mx - radius, my - radius, radius * 2, radius * 2);
  } else {
    ctx.fillStyle = "#12161c";
    ctx.fillRect(mx - radius, my - radius, radius * 2, radius * 2);
  }
  ctx.restore();

  ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(mx, my, radius - 2, Math.PI * 0.05, Math.PI * 0.95);
  ctx.stroke();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(mx, my, radius - 3, Math.PI * 1.05, Math.PI * 1.95);
  ctx.stroke();

  ctx.lineWidth = 2;
  ctx.strokeStyle = theme.ringInner;
  ctx.beginPath();
  ctx.arc(mx, my, radius - 1, Math.PI * 1.1, Math.PI * 1.9);
  ctx.stroke();
}

export function drawShareCard(
  canvas,
  {
    correct,
    total,
    score,
    mode,
    sportLabel,
    username,
    shareToken,
    verifyUrl,
    logo = null,
    tier,
  }
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const resolvedTier = resolveShareCardTier({ mode, correct, total, tier });
  const theme = getShareCardTheme(resolvedTier);
  const tag = resolveShareCardTag({ mode, tier: resolvedTier });

  const width = SHARE_CARD_WIDTH;
  const height = SHARE_CARD_HEIGHT;
  canvas.width = width * SHARE_CARD_SCALE;
  canvas.height = height * SHARE_CARD_SCALE;
  ctx.setTransform(SHARE_CARD_SCALE, 0, 0, SHARE_CARD_SCALE, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const background = ctx.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, "#181c22");
  background.addColorStop(0.5, "#0d1014");
  background.addColorStop(1, "#101319");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  ctx.lineWidth = 2;
  ctx.strokeStyle = theme.frame;
  roundRect(ctx, 30, 30, width - 60, height - 60, 30);
  ctx.stroke();

  ctx.textAlign = "left";
  ctx.font = "700 34px Inter, sans-serif";
  ctx.fillStyle = createThemeFoil(ctx, theme, 60, 0, 360, 0);
  ctx.fillText(spacedWordmark("STATS.FAN"), 56, 96);

  ctx.font = "600 16px Inter, sans-serif";
  const tagWidth = ctx.measureText(tag).width + 36;
  const tagX = width - 56 - tagWidth;
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = theme.tagStroke;
  roundRect(ctx, tagX, 58, tagWidth, 40, 20);
  ctx.stroke();
  ctx.fillStyle = theme.accent;
  ctx.textBaseline = "middle";
  ctx.fillText(tag, tagX + 18, 78);
  ctx.textBaseline = "alphabetic";

  const medalY = 276;
  const medalR = 112;
  drawMedallion(ctx, logo, width / 2, medalY, medalR, theme);

  const gradeTop = shareCardGradeTop(medalY, medalR);
  const rest = username ? 4 : 3;
  const [headlineY, recordY, scoreY, issuedY] = stackYs(
    gradeTop + 108,
    height - 214,
    rest
  );

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = createThemeFoil(
    ctx,
    theme,
    width / 2 - 180,
    gradeTop,
    width / 2 + 180,
    gradeTop + 88
  );
  ctx.font = '700 76px "Black Ops One", "Varsity One BC", sans-serif';
  ctx.fillText(formatShareCardPercent(correct, total), width / 2, gradeTop);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = createThemeFoil(ctx, theme, width / 2 - 220, headlineY - 28, width / 2 + 220, headlineY + 8);
  ctx.font = "italic 600 30px Inter, Georgia, serif";
  ctx.fillText(buildShareCardHeadline(sportLabel, mode), width / 2, headlineY);

  ctx.fillStyle = INK;
  ctx.font = "700 40px Inter, ui-monospace, monospace";
  ctx.fillText(`${correct} / ${total}`, width / 2, recordY);

  ctx.fillStyle = MUTED;
  ctx.font = "500 22px Inter, ui-monospace, monospace";
  ctx.fillText(`SCORE  ${score}`, width / 2, scoreY);

  if (username && issuedY) {
    ctx.fillStyle = theme.accent;
    ctx.fillText(`ISSUED TO  @${username}`, width / 2, issuedY);
  }

  ctx.strokeStyle = theme.frameSoft;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(56, height - 188);
  ctx.lineTo(width - 56, height - 188);
  ctx.stroke();

  const qrSize = 118;
  const qrX = width - 56 - qrSize;
  const qrY = height - 56 - qrSize;
  if (verifyUrl) {
    drawStyledQr(ctx, qrX, qrY, qrSize, verifyUrl, theme);
  }

  ctx.textAlign = "left";
  ctx.font = "600 16px Inter, ui-monospace, monospace";
  ctx.fillStyle = theme.accent;
  ctx.fillText("VERIFIABLE", 56, height - 82);
  ctx.fillStyle = MUTED;
  ctx.font = "500 16px Inter, ui-monospace, monospace";
  ctx.fillText(formatShareCardLink(shareToken) || "stats.fan", 56, height - 54);
}

export function drawShareCardBack(
  canvas,
  { correct, total, score, mode, sportLabel, username, logo = null, tier }
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const resolvedTier = resolveShareCardTier({ mode, correct, total, tier });
  const theme = getShareCardTheme(resolvedTier);
  const width = SHARE_CARD_WIDTH;
  const height = SHARE_CARD_HEIGHT;
  canvas.width = width * SHARE_CARD_SCALE;
  canvas.height = height * SHARE_CARD_SCALE;
  ctx.setTransform(SHARE_CARD_SCALE, 0, 0, SHARE_CARD_SCALE, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const background = ctx.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, "#181c22");
  background.addColorStop(0.5, "#0d1014");
  background.addColorStop(1, "#101319");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  ctx.lineWidth = 2;
  ctx.strokeStyle = theme.frame;
  roundRect(ctx, 30, 30, width - 60, height - 60, 30);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.font = "500 20px Inter, ui-monospace, monospace";
  ctx.fillStyle = theme.accent;
  ctx.fillText("C E R T I F I E D   O N", width / 2, 148);
  ctx.font = "700 44px Inter, sans-serif";
  ctx.fillStyle = createThemeFoil(ctx, theme, width / 2 - 180, 180, width / 2 + 180, 230);
  ctx.fillText("STATS.FAN", width / 2, 222);

  drawMedallion(ctx, logo, width / 2, 430, 118, theme);

  const [blurbY, recordY, issuedY] = stackYs(620, 820, 3);
  ctx.font = "italic 500 26px Inter, Georgia, serif";
  ctx.fillStyle = MUTED;
  ctx.fillText("a verified stats.fan trivia result", width / 2, blurbY);
  ctx.font = "700 34px Inter, ui-monospace, monospace";
  ctx.fillStyle = theme.grade;
  ctx.fillText(
    `${formatShareCardPercent(correct, total)}  ·  ${correct} / ${total}`,
    width / 2,
    recordY
  );
  ctx.font = "500 20px Inter, ui-monospace, monospace";
  ctx.fillStyle = theme.accent;
  ctx.fillText(
    username ? `ISSUED TO  @${username}` : buildShareCardHeadline(sportLabel, mode),
    width / 2,
    issuedY
  );

  ctx.strokeStyle = theme.frameSoft;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(60, height - 150);
  ctx.lineTo(width - 60, height - 150);
  ctx.stroke();
  ctx.font = "400 17px Inter, sans-serif";
  ctx.fillStyle = MUTED;
  ctx.fillText("scan the front or open the verify link", width / 2, height - 108);
  ctx.fillText("to confirm this score on stats.fan", width / 2, height - 84);
}

export async function paintShareCard(canvas, props) {
  if (!canvas) return;
  if (typeof document !== "undefined" && document.fonts?.ready) {
    await document.fonts.ready.catch(() => {});
  }
  const logo = await loadShareImage(SHARE_CARD_LOGO_SRC).catch(() => null);
  drawShareCard(canvas, { ...props, logo });
}

export async function paintShareCardFaces(props) {
  if (typeof document !== "undefined" && document.fonts?.ready) {
    await document.fonts.ready.catch(() => {});
  }
  const logo = await loadShareImage(SHARE_CARD_LOGO_SRC).catch(() => null);
  const front = document.createElement("canvas");
  const back = document.createElement("canvas");
  drawShareCard(front, { ...props, logo });
  drawShareCardBack(back, { ...props, logo });
  return { front, back };
}

export const SHARE_CARD_LOGO_SRC = BRAND_ASSETS.icon;
